// @ts-check
import { useEffect, useState } from "react"
import { useReducedMotion } from "framer-motion"

const STEP_MS = 550

/**
 * @typedef {{ number: number, edit: { id?: string | null, old_string?: string | null, new_string?: string | null, rationale?: string | null } }} Draft
 */

/**
 * Where an edit lands in the document. One that cannot be placed goes last.
 * @param {string} content
 * @param {Draft} draft
 */
function draftPosition(content, draft) {
  const index = draft.edit.old_string ? content.indexOf(draft.edit.old_string) : -1
  return index === -1 ? Number.POSITIVE_INFINITY : index
}

/**
 * The drafts still to reveal, in reveal order: down the page from the last one
 * shown, then any that landed above it. The model sends a review's edits in no
 * fixed order, so arrival order would jump around the document.
 * @param {Draft[]} drafted
 * @param {Draft[]} revealed
 * @param {string} content
 */
export function playbackOrder(drafted, revealed, content) {
  const shown = new Set(revealed.map(draft => draft.number))
  const waiting = drafted
    .filter(draft => !shown.has(draft.number))
    .sort((a, b) => draftPosition(content, a) - draftPosition(content, b))
  const last = revealed.at(-1)
  const lastPosition = last ? draftPosition(content, last) : 0
  return [
    ...waiting.filter(draft => draftPosition(content, draft) >= lastPosition),
    ...waiting.filter(draft => draftPosition(content, draft) < lastPosition),
  ]
}

/**
 * Reveals drafted edits one at a time, in document order. The model's tool call
 * can arrive in one piece, so without this all the suggestions would appear at
 * the same moment. The first reveal also waits a step, so that a whole batch
 * has arrived before the order is chosen. With reduced motion, or for a review
 * that already ended, all show at once.
 * @param {Draft[]} drafted drafted edits so far
 * @param {string} content the document the edits apply to
 * @param {boolean} isLive whether a review is running now
 * @returns {Draft[]} the drafts to show, in reveal order
 */
export function useEditPlayback(drafted, content, isLive) {
  const reduceMotion = useReducedMotion()
  const [revealed, setRevealed] = useState(/** @type {Draft[]} */([]))

  useEffect(() => {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer
    if (drafted.length < revealed.length) {
      setRevealed([]) // a new run cleared the drafts
    } else if (revealed.length < drafted.length) {
      const order = playbackOrder(drafted, revealed, content)
      if (reduceMotion || (!isLive && revealed.length === 0)) setRevealed(order)
      else timer = setTimeout(() => setRevealed([...revealed, order[0]]), STEP_MS)
    }
    return () => clearTimeout(timer)
  }, [drafted, content, revealed, isLive, reduceMotion])

  return revealed.length > drafted.length ? [] : revealed
}

/**
 * The suggestions to show while edits play in: earlier suggestions, then the
 * drafts playback has reached. A saved suggestion replaces its draft, and once
 * any draft is saved the ones the server skipped are dropped.
 * @param {Array<{ id: string }>} pendingDiffs
 * @param {Draft[]} drafted
 * @param {Draft[]} revealed
 */
export function mergeDraftedEdits(pendingDiffs, drafted, revealed) {
  const saved = new Map(pendingDiffs.map(diff => [diff.id, diff]))
  const draftId = (/** @type {Draft} */ draft) => draft.edit.id || `draft-${draft.number}`
  const draftIds = new Set(drafted.map(draftId))
  const anySaved = drafted.some(draft => saved.has(draftId(draft)))

  const shown = []
  for (const draft of revealed) {
    const id = draftId(draft)
    const savedDiff = saved.get(id)
    if (savedDiff) shown.push(savedDiff)
    else if (!anySaved) {
      shown.push({
        id,
        old_string: draft.edit.old_string ?? "",
        new_string: draft.edit.new_string ?? "",
        rationale: draft.edit.rationale ?? "",
        status: "drafting",
      })
    }
  }
  return [...pendingDiffs.filter(diff => !draftIds.has(diff.id)), ...shown]
}
